import * as THREE from 'three/webgpu';

import { PartBatch } from '../PartBatch.js';
import { STATE, TAU } from '../ai.js';

/**
 * @file The rig backend: procedural creatures made of rigid PARTS, each part an
 * instanced draw shared by every unit of the type, posed per frame by the
 * creature's own JS (`engine/actors/creatures.js` is the authoring contract).
 *
 *   draw calls = number of distinct part geometries alive ≠ number of units
 *
 * It generalises (and replaces) `MeshEnemyRenderer` — "one InstancedMesh per
 * type" became "one PartBatch per part slot per type", and the two hard-won
 * behaviours carry over verbatim:
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE PROBE TINT IS NORMALISED HERE (and NOT in the sprite backend)
 * ─────────────────────────────────────────────────────────────────────────────
 * A sprite atlas is unlit art, so the raw probe value IS its lighting. A rig
 * part already has full lighting baked into `color`/`color1`; multiplying by a
 * raw probe would light it twice and every creature would blow past the bloom
 * threshold. So the instance tint is divided by the grid's brightest probe:
 * ~1.0 in the open, <1 in an alley — "how dark is this spot" with no double
 * counting. Day and night normalise SEPARATELY so a night probe next to a lit
 * window still reads as a highlight.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * HAND-WRITTEN TRS
 * ─────────────────────────────────────────────────────────────────────────────
 * The hot loop writes column-major matrix elements straight into each batch's
 * `instanceMatrix.array`. With parts, N = units × parts, so the per-instance
 * cost of `Matrix4.compose` matters MORE here than it did for whole enemies.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE GAIT CLOCK IS THE MANAGER'S, NOT OURS
 * ─────────────────────────────────────────────────────────────────────────────
 * `manager.animPhase` (randomised on spawn, advanced only while moving, speed
 * scaled) drives `pose(t)`, and `manager.wobbleAmp` — which damps to 0 on stop
 * — is the blend-to-rest weight: the posed node values are lerped back toward
 * the rest pose by it, so a creature that stops walking settles instead of
 * freezing mid-stride. Do not invent a second clock.
 *
 * `update(dt, u, p)` (the state-reactive hook — turret tracking) runs AFTER
 * the gait blend and is NOT damped: a standing tank still aims.
 *
 * @module engine/actors/renderers/RigEnemyRenderer
 */

/** @typedef {import('../EnemyManager.js').EnemyManager} EnemyManager */
/** @typedef {import('../../types.js').EnemyType} EnemyType */

/** Floor on the normalised tint: nothing in a playable level is truly black. */
const TINT_FLOOR = 0.22;
/** Headroom above the reference, so a probe near a lamp can still pop. */
const TINT_CEIL = 1.35;

export class RigEnemyRenderer {
  /**
   * @param {Object} o
   * @param {EnemyType[]} o.types Same array (same order) the manager indexes.
   * @param {number} [o.max=512] Per-part instance cap; match the manager's pool.
   * @param {THREE.Object3D|null} [o.parent]
   * @param {import('../../types.js').LightProbeGrid|null} [o.probes] Used ONLY
   *   to derive the normalisation references (one pass at construction).
   * @param {string} [o.name='enemyRigs']
   */
  constructor(o = {}) {
    const { types = [], max = 512, parent = null, probes = null, name = 'enemyRigs' } = o;

    /** @type {EnemyType[]} */
    this.types = types;
    /** @type {THREE.Group} */
    this.object = new THREE.Group();
    this.object.name = name;
    this.object.matrixAutoUpdate = false;
    this.object.updateMatrix();

    const ref = probeReference(probes);
    this._invRefD = ref.day;
    this._invRefN = ref.night;

    /**
     * One entry per type carrying a rig, `null` otherwise.
     * @type {Array<{rig: import('../../types.js').EnemyRigBackend, batches: PartBatch[]}|null>}
     */
    this.entries = types.map((t, i) => this._buildEntry(t, i, max, name));

    /**
     * Per pool-slot unit state (nodes + persistent `mem`), created lazily the
     * first frame a slot is seen alive with a rig type. `_alive` tracks the
     * previous frame so a slot reused by a respawn gets fresh `mem`.
     * @type {Array<{typeIdx: number, nodes: any[], byName: Object, view: any}|null>}
     */
    this._units = new Array(max).fill(null);
    this._alive = new Uint8Array(max);

    this._visible = true;
    /** @type {Uint8Array|null} Per-type draw permission; null = all. */
    this._mask = null;
    this._drawn = 0;
    this._disposed = false;

    parent?.add(this.object);
  }

  /**
   * @param {EnemyType} type @param {number} i @param {number} max @param {string} name
   */
  _buildEntry(type, i, max, name) {
    const rig = type?.rig;
    if (!rig?.parts?.length) return null;
    const batches = rig.parts.map((p, j) => {
      const b = new PartBatch({ geometry: p.geometry, max, name: `${name}:${type.id ?? i}:${p.name ?? j}` });
      this.object.add(b.mesh);
      return b;
    });
    return { rig, batches };
  }

  /**
   * Rebuilds every part instance from the manager's arrays, running each live
   * unit's `pose` + `update`.
   *
   * @param {EnemyManager} manager
   * @param {Object} [o]
   * @param {number} [o.camYaw] Unused (parts face their travel direction);
   *   accepted so all three renderers share one call site.
   * @param {number} [o.dt=0] Step seconds — forwarded to `update`.
   * @param {{position: {x:number,y:number,z:number}}|null} [o.player] Hero-mode
   *   target for `update`'s unit view (`ctx.player` equivalent).
   * @param {'player'|'teams'} [o.targeting='player'] Where a unit's live target
   *   comes from: the player, or `manager.target` (team targeting).
   */
  sync(manager, o = {}) {
    if (this._disposed) return;
    const dt = o.dt ?? 0;
    const player = o.player ?? null;
    const teams = o.targeting === 'teams';
    const entries = this.entries;
    for (const e of entries) if (e) for (const b of e.batches) b.reset();

    const mask = this._mask;
    if (this._visible) {
      for (let i = 0; i < manager.top; i++) {
        const st = manager.state[i];
        if (st === STATE.DEAD) {
          this._alive[i] = 0;
          continue;
        }
        const ti = manager.typeIdx[i];
        if (mask && !mask[ti]) {
          this._alive[i] = 0;
          continue;
        }
        const e = entries[ti];
        if (!e) {
          this._alive[i] = 0;
          continue;
        }

        // ── unit state (lazy; fresh `mem` on respawn or type change) ─────────
        let unit = this._units[i];
        if (!unit || unit.typeIdx !== ti || !this._alive[i]) {
          unit = this._units[i] = makeUnit(ti, e.rig);
        }
        this._alive[i] = 1;

        // ── root TRS ─────────────────────────────────────────────────────────
        const sc = manager.scale[i];
        const yaw = (manager.facing[i] / 256) * TAU;
        const cy = Math.cos(yaw);
        const sy = Math.sin(yaw);
        const r00 = cy * sc;
        const r02 = sy * sc;
        const r20 = -sy * sc;
        const r22 = cy * sc;
        const r11 = sc;
        const rootX = manager.x[i];
        const rootY = manager.y[i] + manager.wobbleBob[i];
        const rootZ = manager.z[i];

        // ── pose ─────────────────────────────────────────────────────────────
        const rig = e.rig;
        const nodes = unit.nodes;
        for (let j = 0; j < nodes.length; j++) resetNode(nodes[j], rig.parts[j].rest);
        if (rig.pose) {
          rig.pose(manager.animPhase[i], unit.byName);
          // Blend to rest by the damped walk amplitude — the gait settles when
          // the unit stops instead of freezing mid-stride.
          const amp = manager.wobbleAmp[i];
          if (amp < 0.999) for (let j = 0; j < nodes.length; j++) blendNode(nodes[j], rig.parts[j].rest, amp);
        }
        if (rig.update) {
          const v = unit.view;
          v.x = rootX;
          v.z = rootZ;
          v.yaw = yaw;
          v.state = st;
          if (teams) {
            const t = manager.target[i];
            if (t >= 0 && manager.state[t] !== STATE.DEAD) {
              v.hasTarget = true;
              v.targetX = manager.x[t];
              v.targetZ = manager.z[t];
            } else {
              v.hasTarget = false;
            }
          } else if (player) {
            v.hasTarget = true;
            v.targetX = player.position.x;
            v.targetZ = player.position.z;
          } else {
            v.hasTarget = false;
          }
          rig.update(dt, v, unit.byName);
        }

        // ── tint (normalised probes — see the header) ────────────────────────
        const src = i * 3;
        const kd = this._invRefD;
        const kn = this._invRefN;
        const dR = clampTint(manager.tintD[src] * kd);
        const dG = clampTint(manager.tintD[src + 1] * kd);
        const dB = clampTint(manager.tintD[src + 2] * kd);
        const nR = clampTint(manager.tintN[src] * kn);
        const nG = clampTint(manager.tintN[src + 1] * kn);
        const nB = clampTint(manager.tintN[src + 2] * kn);
        const flash = manager.flash[i];

        // ── compose world matrices, push into the part batches ───────────────
        for (let j = 0; j < nodes.length; j++) {
          const b = e.batches[j];
          const slot = b.alloc();
          if (slot < 0) continue;
          const n = nodes[j];

          // Local rotation, THREE Euler 'XYZ' (matches the bake-time rest
          // matrix convention), columns scaled per axis.
          const ca = Math.cos(n.rx);
          const sa = Math.sin(n.rx);
          const cb = Math.cos(n.ry);
          const sb = Math.sin(n.ry);
          const cc = Math.cos(n.rz);
          const scz = Math.sin(n.rz);
          const ae = ca * cc;
          const af = ca * scz;
          const be = sa * cc;
          const bf = sa * scz;
          // Column 0
          let l0 = cb * cc * n.sx;
          let l1 = (af + be * sb) * n.sx;
          let l2 = (bf - ae * sb) * n.sx;
          // Column 1
          let l4 = -cb * scz * n.sy;
          let l5 = (ae - bf * sb) * n.sy;
          let l6 = (be + af * sb) * n.sy;
          // Column 2
          let l8 = sb * n.sz;
          let l9 = -sa * cb * n.sz;
          let l10 = ca * cb * n.sz;

          const m = b.mArr;
          const off = slot * 16;
          // world = root(Ry·sc + T) × local
          m[off] = r00 * l0 + r02 * l2;
          m[off + 1] = r11 * l1;
          m[off + 2] = r20 * l0 + r22 * l2;
          m[off + 3] = 0;
          m[off + 4] = r00 * l4 + r02 * l6;
          m[off + 5] = r11 * l5;
          m[off + 6] = r20 * l4 + r22 * l6;
          m[off + 7] = 0;
          m[off + 8] = r00 * l8 + r02 * l10;
          m[off + 9] = r11 * l9;
          m[off + 10] = r20 * l8 + r22 * l10;
          m[off + 11] = 0;
          m[off + 12] = r00 * n.px + r02 * n.pz + rootX;
          m[off + 13] = r11 * n.py + rootY;
          m[off + 14] = r20 * n.px + r22 * n.pz + rootZ;
          m[off + 15] = 1;

          const dst = slot * 3;
          const D = b.colD.array;
          const N = b.colN.array;
          D[dst] = dR;
          D[dst + 1] = dG;
          D[dst + 2] = dB;
          N[dst] = nR;
          N[dst + 1] = nG;
          N[dst + 2] = nB;
          b.flash.array[slot] = flash;
        }
      }
    }

    let drawn = 0;
    for (const e of entries) {
      if (!e) continue;
      let units = 0;
      for (const b of e.batches) {
        b.commit();
        if (b.mesh.count > units) units = b.mesh.count;
      }
      drawn += units;
    }
    this._drawn = drawn;
  }

  /** @param {boolean} v */
  setVisible(v) {
    this._visible = !!v;
    this.object.visible = this._visible;
  }

  /**
   * Restricts drawing to selected type indices.
   * @param {Uint8Array|null} mask One byte per type, non-zero = draw. `null`
   *   restores "every type".
   */
  setTypeMask(mask) {
    this._mask = mask ?? null;
  }

  /** @returns {boolean} True iff at least one type carries a rig backend. */
  get hasAny() {
    for (const e of this.entries) if (e) return true;
    return false;
  }

  /**
   * @returns {{backend: string, drawn: number, draws: number, tris: number,
   *   partsPerType: Object<string, number>}} `partsPerType` is the draw budget
   *   made visible — report it, so a twenty-part creature cannot land silently.
   */
  stats() {
    let draws = 0;
    let tris = 0;
    /** @type {Object<string, number>} */
    const partsPerType = {};
    for (let i = 0; i < this.entries.length; i++) {
      const e = this.entries[i];
      if (!e) continue;
      partsPerType[this.types[i]?.id ?? i] = e.rig.partCount;
      for (let j = 0; j < e.batches.length; j++) {
        const b = e.batches[j];
        if (b.mesh.count === 0) continue;
        draws++;
        tris += (e.rig.parts[j].tris ?? 0) * b.mesh.count;
      }
    }
    return { backend: 'rig', drawn: this._drawn, draws, tris, partsPerType };
  }

  /** Idempotent. Does NOT dispose part geometries — the rig backends own those. */
  dispose() {
    if (this._disposed) return;
    this._disposed = true;
    for (const e of this.entries) {
      if (!e) continue;
      for (const b of e.batches) b.dispose();
    }
    this.entries.length = 0;
    this._units.length = 0;
    this.object.removeFromParent();
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Internals
// ─────────────────────────────────────────────────────────────────────────────

/**
 * @param {number} typeIdx
 * @param {import('../../types.js').EnemyRigBackend} rig
 */
function makeUnit(typeIdx, rig) {
  const nodes = rig.parts.map(() => ({
    px: 0, py: 0, pz: 0, rx: 0, ry: 0, rz: 0, sx: 1, sy: 1, sz: 1,
    // Author-facing views (p.arm.rotation.z = …) aliasing the flat fields.
    position: /** @type {any} */ (null),
    rotation: /** @type {any} */ (null),
    scale: /** @type {any} */ (null),
  }));
  /** @type {Object<string, any>} */
  const byName = {};
  for (let j = 0; j < nodes.length; j++) {
    const n = nodes[j];
    // Property aliases keep the hot loop on flat fields while `pose` writes the
    // ergonomic `p.arm.rotation.z` form.
    n.position = {
      get x() { return n.px; }, set x(v) { n.px = v; },
      get y() { return n.py; }, set y(v) { n.py = v; },
      get z() { return n.pz; }, set z(v) { n.pz = v; },
    };
    n.rotation = {
      get x() { return n.rx; }, set x(v) { n.rx = v; },
      get y() { return n.ry; }, set y(v) { n.ry = v; },
      get z() { return n.rz; }, set z(v) { n.rz = v; },
    };
    n.scale = {
      get x() { return n.sx; }, set x(v) { n.sx = v; },
      get y() { return n.sy; }, set y(v) { n.sy = v; },
      get z() { return n.sz; }, set z(v) { n.sz = v; },
    };
    byName[rig.parts[j].name] = n;
  }
  return {
    typeIdx,
    nodes,
    byName,
    view: { x: 0, z: 0, yaw: 0, state: 0, hasTarget: false, targetX: 0, targetZ: 0, mem: {} },
  };
}

/**
 * @param {any} n
 * @param {{x:number,y:number,z:number,rx:number,ry:number,rz:number}} rest
 */
function resetNode(n, rest) {
  n.px = rest.x;
  n.py = rest.y;
  n.pz = rest.z;
  n.rx = rest.rx;
  n.ry = rest.ry;
  n.rz = rest.rz;
  n.sx = 1;
  n.sy = 1;
  n.sz = 1;
}

/**
 * Lerp back toward rest by `1 - amp` — the blend-to-rest that stops a gait
 * mid-stride freeze.
 * @param {any} n @param {any} rest @param {number} amp 0..1
 */
function blendNode(n, rest, amp) {
  const k = 1 - amp;
  n.px += (rest.x - n.px) * k;
  n.py += (rest.y - n.py) * k;
  n.pz += (rest.z - n.pz) * k;
  n.rx += (rest.rx - n.rx) * k;
  n.ry += (rest.ry - n.ry) * k;
  n.rz += (rest.rz - n.rz) * k;
  n.sx += (1 - n.sx) * k;
  n.sy += (1 - n.sy) * k;
  n.sz += (1 - n.sz) * k;
}

/** @param {number} v @returns {number} */
function clampTint(v) {
  return v < TINT_FLOOR ? TINT_FLOOR : v > TINT_CEIL ? TINT_CEIL : v;
}

/**
 * Brightest probe in each channel, as a RECIPROCAL luminance. One linear pass
 * over the grid at construction; the grids are ~20k floats, so it is free.
 * (Carried over from `MeshEnemyRenderer`, which this file replaces.)
 *
 * @param {import('../../types.js').LightProbeGrid|null} probes
 * @returns {{day: number, night: number}} `1 / reference` per channel.
 */
function probeReference(probes) {
  const out = { day: 1, night: 1 };
  if (!probes?.day || !probes?.night) return out;
  let maxD = 0;
  let maxN = 0;
  const D = probes.day;
  const N = probes.night;
  for (let i = 0; i < D.length; i += 3) {
    const ld = 0.2126 * D[i] + 0.7152 * D[i + 1] + 0.0722 * D[i + 2];
    if (ld > maxD) maxD = ld;
    const ln = 0.2126 * N[i] + 0.7152 * N[i + 1] + 0.0722 * N[i + 2];
    if (ln > maxN) maxN = ln;
  }
  if (maxD > 1e-4) out.day = 1 / maxD;
  if (maxN > 1e-4) out.night = 1 / maxN;
  return out;
}
